import {
  Actions,
  Attributes,
  AttributesRow,
  Button,
  ButtonGroup,
  Card,
  closeDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, Heading, Hide, HStack, Show, Text, VStack} from 'platform/foundation';
import {DeepPartial} from 'utility-types';

import {useEffect, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';

import {
  useGetSelectableMaterialItemQuery,
  useGetSelectableMaterialsIntegrationsQuery,
  useGetTenantQuery,
  usePairSelectableMaterialItemMutation,
  usePatchSelectableMaterialItemMutation,
  usePostSelectableMaterialItemMutation,
  useUnpairSelectableMaterialItemMutation,
} from '@dms/api/core';
import {useGetAuthorizationProfilesByIntegrationIdQuery} from '@dms/api/security';
import {GetArticleResponse, useGetWarehouseQuery, useLazyGetArticleQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError, logError, refreshDmsDatagrid} from '@dms/shared';

import {generateHashFromObjects, useBoolean} from 'shared/web';

import {SELECTABLE_MATERIAL_ITEM_DETAIL} from '../constants/SelectableMaterialItemDetail';
import {
  SelectableMaterialItemDetailForm,
  SelectableMaterialItemDetailFormSchema,
} from '../types/SelectableMaterialItemDetailForm';
import {WarehouseArticleData} from '../types/WarehouseArticleData';
import {PairWarehouseArticleModal} from './PairWarehouseArticleModal';

interface SelectableMaterialItemDetailProps {
  selectableMaterialId: string;
  integrationId: string;
  selectableMaterialItemId?: string;
  warehouseId?: string;
}

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function SelectableMaterialItemDetail(props: SelectableMaterialItemDetailProps) {
  const isDraft = isNil(props.selectableMaterialItemId);

  const [isPairingModalVisible, openPairingModal, closePairingModal] = useBoolean(false);

  const [warehouseArticle, setWarehouseArticle] = useState<GetArticleResponse | null>(null);

  const selectableMaterialItemQuery = useGetSelectableMaterialItemQuery(
    {
      selectableMaterialId: props.selectableMaterialId,
      selectableMaterialItemId: props.selectableMaterialItemId as string,
    },
    {skip: isDraft}
  );

  const {integrationOptions, isLoadingIntegrations} = useGetSelectableMaterialsIntegrationsQuery(
    undefined,
    {
      selectFromResult: (result) => ({
        integrationOptions:
          result.data?.map((integration) => ({
            value: integration.integrationId,
            label: integration.name,
          })) ?? [],
        isLoadingIntegrations: result.isLoading,
      }),
    }
  );

  const tenantQuery = useGetTenantQuery();

  const {authorizationProfileOptions, isLoadingAuthorizationProfiles} =
    useGetAuthorizationProfilesByIntegrationIdQuery(
      {integrationId: props.integrationId, 'x-tenant': tenantQuery.data?.id as string},
      {
        skip: isNil(tenantQuery.data?.id),
        selectFromResult: (result) => ({
          authorizationProfileOptions:
            result.data?.map((authorizationProfile) => ({
              value: authorizationProfile.authorizationProfileId,
              label: authorizationProfile.name,
            })) ?? [],
          isLoadingAuthorizationProfiles: result.isLoading,
        }),
      }
    );

  const warehouseQuery = useGetWarehouseQuery(
    {warehouseId: warehouseArticle?.warehouseId as string},
    {skip: isNil(warehouseArticle?.warehouseId)}
  );

  const [getArticle, {isLoading: isLoadingArticle, isError: hasArticleError}] =
    useLazyGetArticleQuery();
  const [pairItem, {isLoading: isPairing}] = usePairSelectableMaterialItemMutation();
  const [unpairItem, {isLoading: isUnpairing}] = useUnpairSelectableMaterialItemMutation();
  const [postItem] = usePostSelectableMaterialItemMutation();
  const [patchItem] = usePatchSelectableMaterialItemMutation();

  // Load article details for existing item
  useEffect(
    function loadWarehouseArticle() {
      if (isNil(selectableMaterialItemQuery.data?.warehouseArticleId) || isNil(props.warehouseId)) {
        return;
      }

      getArticle({
        warehouseId: props.warehouseId,
        articleId: selectableMaterialItemQuery.data.warehouseArticleId,
      })
        .unwrap()
        .then((article) => setWarehouseArticle(article))
        .catch(handleApiError);
    },
    [getArticle, props.warehouseId, selectableMaterialItemQuery.data?.warehouseArticleId]
  );

  const isLoading =
    selectableMaterialItemQuery.isLoading ||
    isLoadingIntegrations ||
    isLoadingAuthorizationProfiles ||
    warehouseQuery.isLoading ||
    isLoadingArticle ||
    isPairing ||
    isUnpairing;

  const hasError = selectableMaterialItemQuery.isError || warehouseQuery.isError || hasArticleError;

  const defaultValues: DeepPartial<SelectableMaterialItemDetailForm> = {
    generalInformation: {
      integrationId: props.integrationId,
      authorizationProfileIds: selectableMaterialItemQuery.data?.authorizationProfileIds,
    },
    externalSundry: {
      name: selectableMaterialItemQuery.data?.externalSundry.name,
      code: selectableMaterialItemQuery.data?.externalSundry.code,
      type: selectableMaterialItemQuery.data?.externalSundry.type,
      specification: selectableMaterialItemQuery.data?.externalSundry.specification,
      unit: selectableMaterialItemQuery.data?.externalSundry.unit,
    },
    warehouseArticleId: selectableMaterialItemQuery.data?.warehouseArticleId,
  };

  const warehouseArticleAttributes: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.catalogNumber'),
      value: warehouseArticle?.manufacturerNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.name'),
      value: warehouseArticle?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.warehouse'),
      value: warehouseQuery.data?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.manufacturer'),
      value: warehouseArticle?.manufacturerName,
    },
  ];

  const handlePairWarehouseArticle = async (
    warehouseArticleData: WarehouseArticleData,
    formApi: UseFormReturn<SelectableMaterialItemDetailForm>
  ) => {
    if (isDraft) {
      formApi.setValue('warehouseArticleId', warehouseArticleData.articleId);
      return await getArticle({
        articleId: warehouseArticleData.articleId,
        warehouseId: warehouseArticleData.warehouseId,
      })
        .unwrap()
        .then((article) => setWarehouseArticle(article))
        .catch(handleApiError);
    }

    const articleResult = await getArticle({
      articleId: warehouseArticleData.articleId,
      warehouseId: warehouseArticleData.warehouseId,
    })
      .unwrap()
      .catch(handleApiError);

    if (isNil(articleResult)) {
      return logError('article is not defined');
    }

    if (isNil(props.selectableMaterialItemId)) {
      return logError('selectableMaterialItemId is not defined');
    }

    await pairItem({
      selectableMaterialId: props.selectableMaterialId,
      body: {
        selectableMaterialItemIds: [props.selectableMaterialItemId],
        warehouseArticleId: warehouseArticleData.articleId,
      },
    })
      .unwrap()
      .then(() => {
        formApi.setValue('warehouseArticleId', warehouseArticleData.articleId);
        setWarehouseArticle(articleResult as GetArticleResponse);
      })
      .then(() => refreshDmsDatagrid('selectable-materials-external-sundries', {deselectAll: true}))
      .then(() =>
        showNotification.success(i18n.t('entity.settings.notifications.successfullyPaired'))
      )
      .catch(handleApiError);
  };

  const handleUnpairWarehouseArticle = async (
    formApi: UseFormReturn<SelectableMaterialItemDetailForm>
  ) => {
    if (isDraft) {
      formApi.setValue('warehouseArticleId', null);
      return setWarehouseArticle(null);
    }

    if (isNil(props.selectableMaterialItemId)) {
      return logError('selectableMaterialItemId is not defined');
    }

    await unpairItem({
      selectableMaterialId: props.selectableMaterialId,
      body: {
        selectableMaterialItemIds: [props.selectableMaterialItemId],
      },
    })
      .unwrap()
      .then(() => formApi.setValue('warehouseArticleId', null))
      .then(() => setWarehouseArticle(null))
      .then(() => refreshDmsDatagrid('selectable-materials-external-sundries', {deselectAll: true}))
      .then(() =>
        showNotification.success(i18n.t('entity.settings.notifications.successfullyUnpaired'))
      )
      .catch(handleApiError);
  };

  const handleSubmit: FormSubmitHandler<SelectableMaterialItemDetailForm> = async (formValues) => {
    if (isDraft) {
      return await postItem({
        selectableMaterialId: props.selectableMaterialId,
        body: {
          authorizationProfileIds: formValues.generalInformation.authorizationProfileIds,
          externalSundry: formValues.externalSundry,
          warehouseArticleId: formValues.warehouseArticleId,
        },
      })
        .unwrap()
        .then(() =>
          refreshDmsDatagrid('selectable-materials-external-sundries', {deselectAll: true})
        )
        .then(() => closeDialog(SELECTABLE_MATERIAL_ITEM_DETAIL))
        .then(() => showNotification.success(i18n.t('general.notifications.successfullyCreated')))
        .catch(handleApiError);
    }

    if (isNil(props.selectableMaterialItemId)) {
      return logError('selectableMaterialItemId is not defined');
    }

    await patchItem({
      selectableMaterialId: props.selectableMaterialId,
      selectableMaterialItemId: props.selectableMaterialItemId,
      body: {
        authorizationProfileIds: formValues.generalInformation.authorizationProfileIds,
        externalSundry: formValues.externalSundry,
      },
    })
      .unwrap()
      .then(() => refreshDmsDatagrid('selectable-materials-external-sundries', {deselectAll: true}))
      .then(() => closeDialog(SELECTABLE_MATERIAL_ITEM_DETAIL))
      .catch(handleApiError);
  };

  return (
    <Form<SelectableMaterialItemDetailForm>
      key={generateHashFromObjects(selectableMaterialItemQuery.data)}
      experimentalZodSchema={SelectableMaterialItemDetailFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => (
        <DataStatus isLoading={isLoading} isError={hasError} minHeight={125}>
          <Show when={isPairingModalVisible}>
            <PairWarehouseArticleModal
              isLoadingArticle={isLoadingArticle}
              selectableMaterialId={props.selectableMaterialId}
              onDiscard={closePairingModal}
              onPairWarehouseArticle={(warehouseArticleData) =>
                handlePairWarehouseArticle(warehouseArticleData, formApi)
              }
            />
          </Show>

          <VStack spacing={4}>
            <VStack spacing={4}>
              <Heading size={4}>{i18n.t('entity.settings.labels.generalInformation')}</Heading>
              <Grid columns={2}>
                <FormField
                  isDisabled
                  isLoading={isLoadingIntegrations}
                  control={control}
                  name="generalInformation.integrationId"
                  type="choice"
                  label={i18n.t('general.labels.integration')}
                  options={integrationOptions}
                />
                <FormField
                  isLoading={isLoadingAuthorizationProfiles}
                  control={control}
                  name="generalInformation.authorizationProfileIds"
                  type="multiChoice"
                  label={i18n.t('entity.settings.labels.authorizationProfile')}
                  options={authorizationProfileOptions}
                  menuInPortal
                />
              </Grid>
            </VStack>

            <VStack spacing={4}>
              <Heading size={4}>{i18n.t('entity.settings.labels.externalSundryData')}</Heading>
              <Grid columns={2}>
                <FormField
                  isRequired
                  control={control}
                  name="externalSundry.code"
                  type="text"
                  label={i18n.t('entity.settings.labels.sundryCode')}
                />
                <FormField
                  control={control}
                  name="externalSundry.name"
                  type="text"
                  label={i18n.t('entity.settings.labels.sundryName')}
                />
                <FormField
                  control={control}
                  name="externalSundry.type"
                  type="text"
                  label={i18n.t('entity.settings.labels.sundryType')}
                />
                <FormField
                  control={control}
                  name="externalSundry.specification"
                  type="text"
                  label={i18n.t('entity.settings.labels.sundrySpecification')}
                />
                <FormField
                  control={control}
                  name="externalSundry.unit"
                  type="text"
                  label={i18n.t('entity.settings.labels.sundryUnit')}
                />
              </Grid>
            </VStack>

            <VStack spacing={4}>
              <VStack spacing={1}>
                <HStack justify="space-between">
                  <Heading size={4}>{i18n.t('entity.settings.labels.warehouseArticle')}</Heading>
                  <Hide when={isNil(warehouseArticle)}>
                    <Actions
                      actions={[
                        {
                          type: 'button',
                          title: i18n.t('general.actions.change'),
                          variant: 'link',
                          leftIcon: 'navigation/refresh',
                          onClick: openPairingModal,
                        },
                        {
                          type: 'button',
                          title: i18n.t('general.actions.unpair'),
                          variant: 'dangerLink',
                          leftIcon: 'navigation/close',
                          onClick: () => handleUnpairWarehouseArticle(formApi),
                        },
                      ]}
                    />
                  </Hide>
                </HStack>
                <Text size="xSmall" color="tertiary">
                  {i18n.t('entity.settings.labels.warehouseArticleMessage')}
                </Text>
              </VStack>
              <Show when={isNil(warehouseArticle)}>
                <HStack>
                  <Button
                    variant="secondary"
                    leftIcon="content/link"
                    title={i18n.t('entity.settings.labels.pairWarehouseArticle')}
                    onClick={openPairingModal}
                  />
                </HStack>
              </Show>
              <Hide when={isNil(warehouseArticle)}>
                <Card variant="inlineGrey">
                  <Attributes rows={warehouseArticleAttributes} size="third" />
                </Card>
              </Hide>
            </VStack>

            <ButtonGroup align="right">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                onClick={() => closeDialog(SELECTABLE_MATERIAL_ITEM_DETAIL)}
              />
              <FormButton
                type="submit"
                control={control}
                title={isDraft ? i18n.t('general.actions.add') : i18n.t('general.actions.save')}
              />
            </ButtonGroup>
          </VStack>
        </DataStatus>
      )}
    </Form>
  );
}
